import { convertAdType } from '../../../../../configs';
import { convertCreative } from '../../../../../configs/creativeTypes';
import type { TagSheetSaveAdItem, TagSheetSaveCreativeItem } from '../types';
import {
  isAdSuccessfullyCreated,
  isCreativeItemFailed,
  isCreativeSuccessfullyCreated,
  isFailedCreativeEntity,
} from '../utils/createAdsWithCreativesOutcome';
import { GENERIC_BATCH_FAILURE_MESSAGE } from './constants';
import type { AdResultRowDisplay, CreativeResultRowDisplay } from './types';

const getCreativeTypeRaw = (creativeItem: TagSheetSaveCreativeItem): string | undefined => {
  const { creativeEntity } = creativeItem;

  if (isFailedCreativeEntity(creativeEntity)) {
    return creativeEntity.failedType;
  }

  return 'type' in creativeEntity ? creativeEntity.type : undefined;
};

const toCreativeTypeDisplay = (rawType: string | undefined): string =>
  rawType ? convertCreative(rawType).displayName : '';

const toBatchErrorDisplay = (
  error: { code: string; message: string } | null | undefined
): {
  code: string;
  message: string;
} => {
  if (error) {
    return { code: error.code, message: error.message };
  }

  return { code: '', message: GENERIC_BATCH_FAILURE_MESSAGE };
};

export const mapAdItemToAdRowDisplay = (adItem: TagSheetSaveAdItem): AdResultRowDisplay => {
  const isSuccess = isAdSuccessfullyCreated(adItem);
  const { adEntity } = adItem;
  const adId = isSuccess && adEntity.__typename === 'AdV5' ? adEntity.id : '';

  return {
    isSuccess,
    adId,
    adName: adEntity.name ?? '',
    adType: convertAdType(adEntity.type).displayName,
    error: isSuccess ? null : toBatchErrorDisplay(adItem.error),
  };
};

export const mapCreativeItemToCreativeRowDisplay = (
  creativeItem: TagSheetSaveCreativeItem
): CreativeResultRowDisplay => {
  if (isCreativeSuccessfullyCreated(creativeItem)) {
    const { creativeEntity } = creativeItem;

    return {
      isSuccess: true,
      creativeId: 'id' in creativeEntity ? creativeEntity.id : '',
      creativeName: 'name' in creativeEntity ? creativeEntity.name : '',
      creativeType: toCreativeTypeDisplay(getCreativeTypeRaw(creativeItem)),
      error: null,
    };
  }

  if (isCreativeItemFailed(creativeItem)) {
    return {
      isSuccess: false,
      creativeId: '',
      creativeName: creativeItem.creativeEntity.name ?? '',
      creativeType: toCreativeTypeDisplay(getCreativeTypeRaw(creativeItem)),
      error: toBatchErrorDisplay(creativeItem.error),
    };
  }

  return {
    isSuccess: false,
    creativeId: '',
    creativeName: '',
    creativeType: '',
    error: { code: '', message: GENERIC_BATCH_FAILURE_MESSAGE },
  };
};
